import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DeliveryWindowForm, DeliveryWindowVM } from '@geofences/delivery-windows/delivery-window';
import { DeliveryWindowFormDialog } from '@geofences/delivery-windows/delivery-window-form-dialog/delivery-window-form-dialog';
import { DeliveryWindowCreateLogic } from '@geofences/delivery-windows/delivery-windows-create-logic';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { firstValueFrom } from 'rxjs';

import { DeliveryWindowsService } from '@core/services/delivery-windows';
import { GeofenceService } from '@core/services/geofence';

@Component({
  selector: 'app-delivery-windows-table',
  imports: [CommonModule, TableModule, ButtonModule, MessageModule, SkeletonModule, DeliveryWindowFormDialog],
  templateUrl: './delivery-window-table.html',
  styleUrl: './delivery-window-table.scss'
})
export class DeliveryWindowTable implements OnInit {
  private readonly deliveryWindowsAPIs = inject(DeliveryWindowsService);
  private readonly geofenceAPIs = inject(GeofenceService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly createLogic = inject(DeliveryWindowCreateLogic);

  readonly id = input.required<string>();

  readonly showFormDialog = signal(false);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly selectedDeliveryWindow = signal<DeliveryWindowVM | undefined>(undefined);
  readonly saving = signal(false);
  readonly deletingId = signal<number | null>(null);
  readonly loading = signal(false);

  readonly columns = signal([
    { field: 'startTime', header: 'Start', sortable: true },
    { field: 'endTime', header: 'End', sortable: true },
    { field: 'maxDeliveriesPerDay', header: 'Max Deliveries', sortable: false },
    { field: 'actions', header: 'Actions', sortable: false }
  ]);

  readonly deliveryWindows = toSignal(this.deliveryWindowsAPIs.deliveryWindows$, {
    initialValue: []
  });

  async ngOnInit(): Promise<void> {
    await this.loadDeliveryWindows();
  }

  private async getGeofence() {
    const id = this.id();
    if (!id) return null;

    try {
      const response = await firstValueFrom(this.geofenceAPIs.getById(id));
      return { geofenceId: response.data.geofenceId, ianaZone: response.data.ianaZone };
    } catch {
      return null;
    }
  }

  async loadDeliveryWindows(): Promise<void> {
    this.loading.set(true);
    try {
      await firstValueFrom(this.deliveryWindowsAPIs.getAll(this.id()));
    } finally {
      this.loading.set(false);
    }
  }

  onCreateDeliveryWindow(): void {
    this.dialogMode.set('create');
    this.selectedDeliveryWindow.set(undefined);
    this.showFormDialog.set(true);
  }

  onEditDeliveryWindow(dw: DeliveryWindowVM): void {
    this.dialogMode.set('edit');
    this.selectedDeliveryWindow.set(dw);
    this.showFormDialog.set(true);
  }

  async submitDeliveryWindowData(formData: DeliveryWindowForm): Promise<void> {
    const geofence = await this.getGeofence();
    const selected = this.selectedDeliveryWindow();

    if (!geofence) return;

    this.saving.set(true);
    try {
      if (this.dialogMode() === 'create') {
        await this.createLogic.create(formData, geofence);
      } else {
        if (!selected?.deliveryWindowId) {
          throw new Error('Missing deliveryWindowId');
        }

        await this.createLogic.edit(formData, geofence, selected.deliveryWindowId);
      }

      await this.loadDeliveryWindows();

      this.closeDialog();
    } finally {
      this.saving.set(false);
    }
  }

  cancelSubmission(): void {
    this.closeDialog();
  }

  dialogVisibility(visible: boolean): void {
    this.showFormDialog.set(visible);
  }

  private closeDialog(): void {
    this.showFormDialog.set(false);
    this.selectedDeliveryWindow.set(undefined);
  }

  formatTime(time: string): string {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = +h;
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${suffix}`;
  }

  deleteDeliveryWindow(dw: DeliveryWindowVM): void {
    if (!dw.deliveryWindowId) return;
    this.confirmationService.confirm({
      header: 'Delete delivery window?',
      message: 'This action cannot be undone.',
      accept: async () => {
        this.deletingId.set(dw.deliveryWindowId!);
        try {
          await firstValueFrom(this.deliveryWindowsAPIs.delete(dw.deliveryWindowId!));
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete delivery window'
          });
        } finally {
          this.deletingId.set(null);
        }
      }
    });
  }
}
